import type { GeoJSONSource, LngLatBounds, LngLatBoundsLike, Map as MapLibreMap, MapOptions, NavigationControl } from 'maplibre-gl'

export interface MapConfig {
  onLoad?(): void
  onClose?: () => void
  closeButton?: boolean
}

export interface MapMarker {
  id?: string
  overlayText?: string
  content?: string
  latitude: number
  longitude: number
}

export default class DynamicMap {
  element!: HTMLElement
  isInitialized = false
  markers: MapMarker[] = []
  config!: MapConfig
  mapLatitude!: string | null
  mapLongitude!: string | null
  mapBoundariesBox!: LngLatBoundsLike
  map!: MapLibreMap
  bounds!: LngLatBounds
  private LngLatBounds!: typeof LngLatBounds

  constructor(element: string | HTMLElement, config: MapConfig, markers: MapMarker[]) {
    let el: HTMLElement | string | null = element

    if (typeof element === 'string') {
      el = document.querySelector<HTMLElement>(element)
    }

    if (!el || typeof el === 'string') {
      return
    }

    this.element = el
    this.config = config ?? {}

    this.markers = markers || []

    if (!this.element) {
      return
    }

    this.initMap().catch((error) => {
      console.error('Failed to initialize map:', error)
    })
  }

  async initMap(): Promise<void> {
    const loaderEl = this.element.querySelector<HTMLElement>('.dca-loader')
    const maplibregl = await import('maplibre-gl')

    // @ts-expect-error css import is handled by webpack
    await import('maplibre-gl/dist/maplibre-gl.css')

    const { LngLatBounds, Map: MapLibreMap, NavigationControl } = maplibregl

    this.LngLatBounds = LngLatBounds

    this.mapLatitude = this.element.getAttribute('data-latitude')
    this.mapLongitude = this.element.getAttribute('data-longitude')

    const styleUrl = new URL(this.element.getAttribute('data-style-url') as string)

    const mapConfig: MapOptions = {
      container: this.element,
      zoom: Number(this.element.getAttribute('data-zoom')),
      center: [0, 0],
      renderWorldCopies: false,
      // to enable raster:
      // style: {
      //   version: 8,
      //   sources: {
      //     'simple-tiles': {
      //       type: 'raster',
      //       tiles: ['https://qa.www.campings.com/maps/tile/512/{z}/{x}/{y}.png'],
      //       tileSize: 512,
      //       attribution: 'MapLibre | &copy; OpenStreetMap',
      //     },
      //   },
      //   layers: [
      //     {
      //       id: 'simple-tiles',
      //       type: 'raster',
      //       source: 'simple-tiles',
      //       minzoom: 0,
      //       maxzoom: 22,
      //     },
      //   ],
      // },
      style: styleUrl.toString(),
      // since we can't rewrite the tile server paths in the infra, we have to tweak the map lib requests so they actually go through
      transformRequest: (url) => {
        const prefix = '/maps/assets'

        if (url.startsWith(styleUrl.origin)) {
          const path = url.replace(styleUrl.origin, '')

          if (!path.startsWith(prefix)) {
            return {
              url: `${styleUrl.origin}${prefix}${path}`,
            }
          }
        }

        return { url }
      },
    }

    const mapBoundariesBox = this.element.getAttribute('data-map-boundaries')

    if (mapBoundariesBox) {
      this.mapBoundariesBox = JSON.parse<LngLatBoundsLike>(mapBoundariesBox)
      mapConfig.bounds = this.mapBoundariesBox
    }

    if (this.mapLatitude && this.mapLongitude) {
      mapConfig.center = [parseFloat(this.mapLongitude), parseFloat(this.mapLatitude)]
    }

    const closeButtonLabel = this.element.hasAttribute('data-close-label') ? (this.element.getAttribute('data-close-label') as string) : ''

    const CloseControl = function (this: NavigationControl, callback: MapConfig['onClose']): NavigationControl {
      let container: HTMLElement | undefined

      this.onAdd = function () {
        container = document.createElement('div')
        container.className = 'maplibregl-ctrl maplibregl-ctrl-group'

        const button = document.createElement('button')
        button.className = 'maplibregl-ctrl-zoom-in'
        button.type = 'button'
        button.innerHTML = '<span aria-hidden="true">&times;</span>'
        button.title = closeButtonLabel
        button.setAttribute('data-type', 'close')

        if (callback) {
          button.addEventListener('click', callback)
        }

        container.appendChild(button)

        return container
      }

      this.onRemove = function () {
        container?.parentNode?.removeChild(container)
      }

      return this
    }

    this.map = new MapLibreMap(mapConfig)
    loaderEl?.remove()

    if (this.config?.closeButton && this.config.onClose) {
      // @ts-expect-error to check later
      this.map.addControl(new CloseControl(this.config.onClose))
    }

    this.map.addControl(new NavigationControl())
    setTimeout(() => this.resize(), 0)

    this.map.on('load', () => {
      const markerImageUrl = this.element.getAttribute('data-marker-image')

      if (markerImageUrl) {
        const img = new Image()
        img.crossOrigin = 'Anonymous'

        img.onload = () => {
          if (!this.map.hasImage('custom-marker')) {
            img.width = img.naturalWidth || 35
            img.height = img.naturalHeight || 35

            this.map.addImage('custom-marker', img, { pixelRatio: 2 })

            this.addCampsitesLayer()
          }
        }

        img.onerror = (e) => {
          console.error('Marker image failed to load', e)
          this.addCampsitesLayer()
        }

        img.src = markerImageUrl
      } else {
        this.addCampsitesLayer()
      }
    })

    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.map.resize()
        this.fitBounds()
      }, 250)
    })
  }

  addCampsitesLayer(): void {
    if (this.map.getSource('campsites-source')) {
      return
    }

    this.map.addSource('campsites-source', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    })

    this.map.addLayer({
      id: 'campsites-layer',
      type: 'symbol',
      source: 'campsites-source',
      layout: {
        'icon-image': 'custom-marker',
        'icon-size': 1,
        'icon-allow-overlap': true,
        'icon-anchor': 'bottom',
      },
    })

    if (this.markers.length > 0) {
      this.setMarkers(this.markers)
    }
  }

  resize(): MapLibreMap {
    return this.map.resize()
  }

  setMarkers(markers: MapMarker[]): DynamicMap {
    this.markers = markers

    if (!this.map.getSource('campsites-source')) {
      return this
    }

    const features = markers
      .filter((m) => m.latitude && m.longitude)
      .map((m) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [m.longitude, m.latitude],
        },
        properties: {
          id: m.id,
        },
      }))

    const source = this.map.getSource('campsites-source') as GeoJSONSource
    if (source) {
      source.setData({
        type: 'FeatureCollection',
        // @ts-expect-error typescript might complain about specific Feature types
        features: features,
      })
    }

    if (!this.mapLatitude && !this.mapLongitude && features.length > 0 && !this.mapBoundariesBox) {
      this.fitBounds(markers)
    }

    return this
  }

  clearMarkers(): DynamicMap {
    this.markers = []
    const source = this.map.getSource('campsites-source') as GeoJSONSource
    if (source) {
      source.setData({
        type: 'FeatureCollection',
        features: [],
      })
    }

    return this
  }

  fitBounds(markers?: MapMarker[]): DynamicMap {
    this.bounds = new this.LngLatBounds()

    const markersToFit = markers || this.markers || []

    markersToFit.forEach((marker) => {
      if (marker.longitude && marker.latitude) {
        this.bounds?.extend([marker.longitude, marker.latitude])
      }
    })

    if (!markersToFit.length) {
      return this
    }

    this.map.jumpTo({
      center: this.bounds.getCenter(),
    })

    return this
  }

  getMap(): MapLibreMap {
    return this.map
  }
}
